import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { UiButtonComponent } from '../../atoms/button/button.component';
import { UiCodeInputComponent } from '../../atoms/code-input/code-input.component';
import { UiInputComponent } from '../../atoms/input/input.component';
import { UiMessageComponent } from '../../atoms/message/message.component';
import {
  UiRecaptchaComponent,
  UiRecaptchaSize,
  UiRecaptchaTheme,
} from '../../atoms/recaptcha/recaptcha.component';
import { UiAuthLoginSubmitEvent } from '../auth-login/auth-login.component';

export type UiPortalClientesLoginMode = 'login' | 'personalize' | 'recover';
export type UiPortalRecoverStep = 1 | 2 | 3;

function hasMinLength(value: string): boolean {
  return value.length >= 8;
}

function isAlphanumeric(value: string): boolean {
  return /[A-Za-z]/.test(value) && /\d/.test(value);
}

function hasCasing(value: string): boolean {
  return /[a-z]/.test(value) && /[A-Z]/.test(value);
}

/**
 * Template: pantalla de login para Portal Clientes.
 *
 * Mantiene los mismos controles de autenticación que `ui-auth-login`,
 * incluido reCAPTCHA v2 en modo `login`. Distribuye la pantalla en dos
 * columnas: formulario 40% e imagen 60%. Soporta modo `personalize` para
 * primer acceso con checklist de contraseña (sin captcha) y modo
 * `recover` de tres pasos (sin captcha).
 */
@Component({
  selector: 'ui-portal-clientes-login',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiInputComponent,
    UiCodeInputComponent,
    UiButtonComponent,
    UiMessageComponent,
    UiRecaptchaComponent,
  ],
  templateUrl: './portal-clientes-login.component.html',
  styleUrl: './portal-clientes-login.component.scss',
})
export class UiPortalClientesLoginComponent {
  /** Ruta de la imagen del logo. */
  readonly logoSrc = input('/images/logo_azul.png');

  /** Texto alternativo del logo para accesibilidad. */
  readonly logoAlt = input('Diseño y Gestión');

  /** Título principal del formulario. */
  readonly title = input('Portal de clientes');

  /** Ruta de la imagen del panel derecho. */
  readonly bannerSrc = input('/images/clientes.png');

  /** Texto alternativo de la imagen del panel derecho. */
  readonly bannerAlt = input('Desarrollo inmobiliario');

  /** Site key pública de reCAPTCHA v2 (obligatoria en modo login). */
  readonly recaptchaSiteKey = input.required<string>();

  /** Tema del widget de reCAPTCHA. */
  readonly recaptchaTheme = input<UiRecaptchaTheme>('light');

  /** Tamaño del widget de reCAPTCHA. */
  readonly recaptchaSize = input<UiRecaptchaSize>('normal');

  /** Idioma del widget de reCAPTCHA. */
  readonly recaptchaLanguage = input('es');

  /** Mensaje opcional emitido por el flujo de autenticación consumidor. */
  readonly errorMessage = input('');

  /** Modo visual: login, personalización o recuperación de contraseña. */
  readonly mode = input<UiPortalClientesLoginMode>('login');

  /** Paso visible cuando `mode` es `recover`. */
  readonly recoverStep = input<UiPortalRecoverStep>(1);

  /** Muestra el alta de proveedor bajo recuperar contraseña. Solo login. */
  readonly showSupplierRegister = input(false);

  /** Emite las credenciales cuando el formulario es válido y se envía. */
  readonly submitted = output<UiAuthLoginSubmitEvent>();

  /** Emite cuando el usuario completa la personalización de contraseña. */
  readonly passwordPersonalized = output<void>();

  /** Emite cuando el usuario pulsa "Recuperar contraseña". */
  readonly forgotPassword = output<void>();

  /** Emite cuando el usuario pulsa "Registro" en el alta de proveedor. */
  readonly supplierRegister = output<void>();

  /** Emite cuando el usuario pulsa "Regresar" en personalizar o recuperar. */
  readonly backToLogin = output<void>();

  /** Emite el contacto cuando el usuario pide el código de recuperación. */
  readonly recoverSendCode = output<string>();

  /** Emite el código de 6 dígitos a validar. */
  readonly recoverValidateCode = output<string>();

  /** Emite cuando el usuario guarda la nueva contraseña de recuperación. */
  readonly recoverSavePassword = output<void>();

  protected readonly captcha = viewChild(UiRecaptchaComponent);

  protected readonly username = signal('');
  protected readonly password = signal('');
  protected readonly submittedOnce = signal(false);

  protected readonly captchaToken = signal('');
  protected readonly captchaStatus = signal<'idle' | 'expired' | 'error'>(
    'idle',
  );

  protected readonly newPassword = signal('');
  protected readonly confirmPassword = signal('');
  protected readonly recoverContact = signal('');
  protected readonly recoverCode = signal('');

  protected readonly usernameInvalid = computed(
    () => this.submittedOnce() && !this.username().trim(),
  );

  protected readonly passwordInvalid = computed(
    () => this.submittedOnce() && !this.password().trim(),
  );

  protected readonly captchaInvalid = computed(
    () => this.submittedOnce() && !this.captchaToken(),
  );

  protected readonly captchaMessage = computed(() => {
    const status = this.captchaStatus();
    if (status === 'expired') {
      return 'La verificación expiró. Marca de nuevo la casilla.';
    }
    if (status === 'error') {
      return 'Ocurrió un error con la verificación. Intenta de nuevo.';
    }
    if (this.captchaInvalid()) {
      return 'Confirma que no eres un robot para continuar.';
    }
    return '';
  });

  protected readonly canSubmit = computed(
    () =>
      !!this.username().trim() &&
      !!this.password().trim() &&
      !!this.captchaToken(),
  );

  protected readonly canSubmitPersonalize = computed(() => {
    const next = this.newPassword();
    const confirm = this.confirmPassword();
    return (
      hasMinLength(next) &&
      isAlphanumeric(next) &&
      hasCasing(next) &&
      next.length > 0 &&
      confirm.length > 0 &&
      next === confirm
    );
  });

  protected readonly canSendRecoverCode = computed(
    () => !!this.recoverContact().trim(),
  );

  protected readonly canValidateRecoverCode = computed(
    () => this.recoverCode().replace(/\D/g, '').length === 6,
  );

  /**
   * Resetea el widget de reCAPTCHA. Llamar tras un error de autenticación
   * para que el usuario tenga que resolverlo de nuevo.
   */
  resetCaptcha(): void {
    this.captchaToken.set('');
    this.captchaStatus.set('idle');
    this.captcha()?.reset();
  }

  protected onCaptchaResolved(token: string): void {
    this.captchaToken.set(token);
    this.captchaStatus.set('idle');
  }

  protected onCaptchaExpired(): void {
    this.captchaToken.set('');
    this.captchaStatus.set('expired');
  }

  protected onCaptchaError(): void {
    this.captchaToken.set('');
    this.captchaStatus.set('error');
  }

  protected onSubmit(): void {
    this.submittedOnce.set(true);
    if (!this.canSubmit()) return;
    this.submitted.emit({
      username: this.username(),
      password: this.password(),
      recaptchaToken: this.captchaToken(),
    });
  }

  protected onPersonalizeSubmit(): void {
    if (!this.canSubmitPersonalize()) return;
    this.passwordPersonalized.emit();
  }

  protected onBackToLogin(): void {
    this.newPassword.set('');
    this.confirmPassword.set('');
    this.recoverContact.set('');
    this.recoverCode.set('');
    this.backToLogin.emit();
  }

  protected onRecoverSendCode(): void {
    const contact = this.recoverContact().trim();
    if (!contact) return;
    this.recoverSendCode.emit(contact);
  }

  protected onRecoverValidateCode(): void {
    const code = this.recoverCode().replace(/\D/g, '');
    if (code.length !== 6) return;
    this.recoverValidateCode.emit(code);
  }

  protected onRecoverSavePassword(): void {
    if (!this.canSubmitPersonalize()) return;
    this.recoverSavePassword.emit();
  }
}
